import { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer.jsx';
import PublicHeader from '../components/PublicHeader.jsx';
import { useTitle } from '../lib/useTitle.js';

export default function MarketingLanding() {
  useTitle('AI-Powered Gym Management & Training Platform');

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />

      <Hero />
      <ProductStrip />
      <Audiences />
      <HowItWorks />
      <Features />
      <Differentiator />
      <Testimonials />
      <Pricing />
      <FAQ />
      <GetStarted />
      <Footer />
    </div>
  );
}

// Phone-framed screenshot. `src` lives in /public/marketing/. The placeholder
// screenshots there are from a TEST account (gitignored) - swap in clean
// demo-account shots before deploying.
// Screenshots are 600x1298 WebP (2x the widest 300px slot). The intrinsic
// width/height reserve the box before the bytes arrive, so the page does not
// shift as they load. The hero shot is above the fold on desktop, so it is
// fetched eagerly at high priority; the feature-grid shots stay lazy.
const SHOT_W = 600;
const SHOT_H = 1298;

function PhoneMockup({ src, alt, width = 280, eager = false }) {
  return (
    <div style={{
      width, maxWidth: '100%', flexShrink: 0,
      border: '10px solid #111', borderRadius: 40,
      background: '#111', boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
      overflow: 'hidden', lineHeight: 0,
    }}>
      <img
        src={src}
        alt={alt}
        width={SHOT_W}
        height={SHOT_H}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : undefined}
        style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 30 }}
      />
    </div>
  );
}

function Hero() {
  return (
    <section style={{ padding: '72px 24px 56px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 48, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
        <div style={{ flex: '1 1 420px', maxWidth: 560 }}>
          <div style={{ display: 'inline-block', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 999, padding: '4px 12px', marginBottom: 20, fontWeight: 700 }}>
            Free during beta · Onboarding partner gyms
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.4rem)', margin: '0 0 16px', lineHeight: 1.1 }}>
            A complete workout in 60 seconds. AI-built from your gym's exact equipment.
          </h1>
          <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', color: 'var(--text-dim)', margin: '0 0 28px', lineHeight: 1.5 }}>
            Programming, nutrition coaching, and progress tracking in one app - for lifters who train on their own, and the gyms that want their members to have it all.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/login"><button className="primary" style={{ padding: '14px 28px', fontSize: 16 }}>Try it free</button></Link>
            <Link to="/contact"><button style={{ padding: '14px 28px', fontSize: 16 }}>For gyms: request beta access</button></Link>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 14 }}>No credit card. Free forever for individuals.</div>
        </div>
        <PhoneMockup src="/marketing/screen-quick.webp" alt="Generating an AI workout sized to your time and equipment" width={300} eager />
      </div>
    </section>
  );
}

function ProductStrip() {
  const shots = [
    { src: '/marketing/screen-workout.webp', title: 'Start however you want', desc: 'Blank, a saved routine, or AI - then log sets in seconds on your phone.' },
    { src: '/marketing/screen-nutrition.webp', title: 'Nutrition that adapts', desc: 'Macro targets recalculated from your real weight trend and intake, every week.' },
    { src: '/marketing/screen-progress.webp', title: 'See real progress', desc: 'PRs, streaks, weekly volume, and a 30-day heatmap. Your training, measured.' },
  ];
  return (
    <section style={{ padding: '64px 24px', background: 'var(--bg-elev)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: 32 }}>See it in action</h2>
        <p style={{ textAlign: 'center', color: 'var(--text-dim)', margin: '0 0 48px', fontSize: 17 }}>The member experience: built for your phone, in the gym, between sets.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 32, justifyItems: 'center' }}>
          {shots.map((s) => (
            <div key={s.src} style={{ maxWidth: 300, textAlign: 'center' }}>
              <PhoneMockup src={s.src} alt={s.title} width={260} />
              <h3 style={{ margin: '20px 0 6px', fontSize: 18 }}>{s.title}</h3>
              <p style={{ color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.5, margin: 0 }}>{s.desc}</p>
            </div>
          ))}
        </div>
        {/* Someone who just read three screens is at peak intent. Without this
            the section dead-ends and the only CTA is back up in the hero.
            Mirrors the hero pair so both audiences have a next step. */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 48 }}>
          <Link to="/login"><button className="primary" style={{ padding: '14px 28px', fontSize: 16 }}>Try it free</button></Link>
          <Link to="/contact"><button style={{ padding: '14px 28px', fontSize: 16 }}>For gyms: request beta access</button></Link>
        </div>
      </div>
    </section>
  );
}

// Social proof. Beta-stage: one honest placeholder slot. Replace the quote +
// attribution with a real beta gym/member as soon as there's one to quote;
// do NOT invent testimonials (see the "status claims must be true" rule).
function Testimonials() {
  return (
    <section style={{ padding: '72px 24px', textAlign: 'center' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 20, fontWeight: 700 }}>
          From the beta
        </div>
        <blockquote style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)', lineHeight: 1.5, margin: '0 0 20px', color: 'var(--text)', fontWeight: 500 }}>
          &ldquo;[ Replace with a real beta gym or member quote before launch. ]&rdquo;
        </blockquote>
        <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>Founding partner gym - onboarding now</div>
      </div>
    </section>
  );
}

function Audiences() {
  return (
    <section style={{ padding: '60px 24px', background: 'var(--bg-elev)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', margin: '0 0 16px', fontSize: 32 }}>Two ways to use TrainForge</h2>
        <p style={{ textAlign: 'center', color: 'var(--text-dim)', margin: '0 0 48px', fontSize: 17 }}>Same app. Different paths in.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
          <AudienceCard
            title="For Gym Owners"
            tag="B2B · White-label · Beta"
            desc="Run your gym from one platform. Branded for you, used by your members and trainers."
            items={[
              "AI builds programs from YOUR gym's exact equipment, not a generic library",
              'See which members are at risk of ghosting before they cancel',
              'Members track workouts in 30 seconds on their phone. No laptop, no spreadsheet.',
              'Onboard a new trainer in 2 minutes with role-scoped permissions',
              'White-label app at yourgym.mytrainforge.com, your logo, your colors',
              'AI body composition reads from member progress photos (no competitor has this in B2B)',
              'In-app messaging keeps trainer and member talk out of personal SMS',
            ]}
            cta="Request beta access"
            ctaLink="/contact"
          />
          <AudienceCard
            title="For Individuals"
            tag="Self-serve · No gym needed"
            desc="Train on your own with AI coaching. Use whatever equipment you have: home gym, commercial gym, or bodyweight."
            items={[
              "AI knows your gym's exact equipment and builds programs around it",
              'Generate up to 3 program drafts, compare side by side, pick one',
              'Track body changes with weekly photos plus AI composition reads',
              'Adaptive nutrition: calorie targets auto-tune from your actual TDEE every week',
              'Barcode scanner plus AI photo food logging, instant macros',
              'Travel? Convert any workout to a hotel gym or bodyweight in one tap',
              'Select your gym (Planet Fitness, LA Fitness, Hotel Gym, Home Garage) and equipment auto-fills',
            ]}
            cta="Try it free"
            ctaLink="/login"
          />
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { num: '01', title: 'Sign up or get invited', desc: 'Gym owners request beta access and invite their team. Individuals sign up directly. No gym required.' },
    { num: '02', title: 'Set your profile', desc: 'Tell us your goals, experience, equipment, and schedule. Our onboarding wizard takes about 2 minutes.' },
    { num: '03', title: 'Get an AI program', desc: 'Generate up to 3 personalized programs, compare them side by side, and activate the best one. The AI uses your actual gym equipment.' },
    { num: '04', title: 'Train, track, improve', desc: 'Log workouts, scan barcodes, track PRs and achievements. Your nutrition targets adapt weekly based on your real weight trend.' },
  ];

  return (
    <section style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', margin: '0 0 48px', fontSize: 32 }}>How it works</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32 }}>
          {steps.map((s) => (
            <div key={s.num} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 12, fontFamily: 'var(--font-mono)' }}>{s.num}</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>{s.title}</h3>
              <p style={{ color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.5, margin: 0 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" style={{ padding: '60px 24px', background: 'var(--bg-elev)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', margin: '0 0 16px', fontSize: 32 }}>One platform. Three roles.</h2>
        <p style={{ textAlign: 'center', color: 'var(--text-dim)', margin: '0 0 48px', fontSize: 17 }}>Every feature built for your role.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          <FeatureCard title="Members" items={[
            'AI workout programs from your gym\'s equipment',
            'Draft programs: generate 3 options, compare side by side',
            'Barcode scanner + AI photo food logging',
            'Adaptive nutrition: auto-adjusts weekly based on your weight trend',
            'Weekly nutrition coaching insights',
            'Frequent meals: save and one-tap re-log with saved macros',
            '322 exercises with coaching cues and form instructions',
            'Achievements (17 milestones), weekly streaks, and points',
            'Convert workout to different gym equipment with one tap',
            'Progress page: PRs by body part, e1RM trends, session history',
          ]} />
          <FeatureCard title="Trainers" items={[
            'Dashboard of every assigned member at a glance',
            'Compliance dashboard: adherence %, training status, risk flags',
            'In-app messaging with context cards',
            'Generate or hand-edit AI programs for any member',
            'AI coaching summaries to prep for a session in 30 seconds',
            'View member workout history, PRs, nutrition logs',
            'Progress photos with privacy controls',
          ]} />
          <FeatureCard title="Gym Owners" items={[
            'White-label branding (logo, colors, gym name, subdomain)',
            'Invite, deactivate, or transfer members and trainers',
            'Equipment library with 322 exercises across 60+ equipment types',
            'Feature flags: roll out features gradually per member',
            'Audit log of every admin action',
            'Feedback dashboard: see bug reports and feature requests',
            'Gamification: achievements, weekly streaks, points, rewards',
            'Data export: Strong-compatible CSV + GDPR JSON',
          ]} />
        </div>
      </div>
    </section>
  );
}

// When shipping a new differentiator-worthy feature, add a ClaimCard
// below. The /design skill's step 3 includes a "marketing surfaces"
// sub-step that should fire this prompt at design time, not later.
// Sibling surface to also update: src/pages/About.jsx "What makes it
// different" bullet list.
function Differentiator() {
  return (
    <section style={{ padding: '80px 24px', textAlign: 'center', maxWidth: 1000, margin: '0 auto' }}>
      <h2 style={{ fontSize: 32, margin: '0 0 16px' }}>Replace 4 tools with 1</h2>
      <p style={{ fontSize: 17, color: 'var(--text-dim)', lineHeight: 1.6, margin: '0 0 48px', maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
        Other gyms stitch together Mindbody for management, MyFitnessPal for nutrition, Strong for workouts, and a separate billing tool.
        TrainForge is one platform that does all of it, plus a few things none of them do.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, textAlign: 'left' }}>
        <ClaimCard
          title="Programs from YOUR equipment"
          body="AI generates from the specific gear in your gym: fixed barbells, the brand of cable machine, every dumbbell increment. Snap a photo of a new machine and the AI adds it for you. Most apps pick from a generic library and you adapt; we do the opposite."
        />
        <ClaimCard
          title="Nutrition that measures, not predicts"
          body="Calorie targets recalculate weekly from your actual weight trend + intake, not a generic formula. Only MacroFactor and Carbon Diet Coach do this. Neither is a gym platform."
        />
        <ClaimCard
          title="Travel-gym mode"
          body="Lifting at a hotel this week? Pick Hotel Gym for one session. The exercise picker filters to what's actually there. Your usual gym profile stays intact for tomorrow."
        />
        <ClaimCard
          title="AI reads change in your photos, not your body"
          body="Compare two progress photos and get a body-part-by-body-part breakdown of what visibly changed (shoulders, chest, back, arms, core, legs). Other AI body-comp apps tell you 'you look 18% body fat', which is frequently wrong and anxiety-inducing. Ours never claims absolute state; only direction of change between two photos of you, in calibrated neutral language."
        />
      </div>
    </section>
  );
}

function ClaimCard({ title, body }) {
  return (
    <div style={{
      background: 'var(--bg-elev)',
      border: '1px solid var(--border)',
      borderLeft: '3px solid var(--accent)',
      borderRadius: 'var(--radius-md)',
      padding: 20,
    }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: 'var(--text)' }}>{title}</div>
      <div style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

function Pricing() {
  return (
    <section id="pricing" style={{ padding: '80px 24px', background: 'var(--bg-elev)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: 32 }}>Pricing</h2>
        <p style={{ textAlign: 'center', color: 'var(--text-dim)', margin: '0 0 12px', fontSize: 17 }}>
          Free for everyone during beta. No credit card required.
        </p>
        <p style={{ textAlign: 'center', color: 'var(--text-muted)', margin: '0 0 48px', fontSize: 14 }}>
          Paid plans launch when beta ends. Beta partners lock in founder pricing for life.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
          <PriceCard
            name="Individual"
            price="Free"
            desc="Always free for personal use"
            features={[
              'AI workout programs (3 drafts, compare & pick)',
              'Barcode scanner + AI food photo analysis',
              'Adaptive nutrition coaching',
              'Gym profiles (Planet Fitness, LA Fitness, etc.)',
              '322 exercises with form cues',
              '17 achievements + weekly streaks',
              'Session conversion between gyms',
              'Body measurements + progress photos',
            ]}
          />
          <PriceCard
            name="Gym Starter"
            price="Free during beta"
            desc="For small gyms and personal trainers"
            highlighted
            features={[
              'Everything in Individual',
              'White-label branding',
              'Up to 2 trainers',
              'Up to 50 members',
              'Equipment library',
              'Member + trainer management',
              'Audit log',
            ]}
          />
          <PriceCard
            name="Gym Pro"
            price="Free during beta"
            desc="For larger gyms and multi-location"
            features={[
              'Everything in Starter',
              'Unlimited trainers',
              'Unlimited members',
              'TV leaderboard mode',
              'Feature flags per member',
              'Gamification + rewards',
              'Priority support',
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const items = [
    { q: 'Is TrainForge really free?', a: 'Yes, during beta everything is free for both gyms and individuals. When paid plans launch, the Individual tier stays free forever. Beta gym partners get locked-in founder pricing.' },
    { q: 'What kind of AI does TrainForge use?', a: 'We use OpenAI\'s GPT-4o-mini with Structured Outputs for workout programming, food analysis, and coaching. Our nutrition system calculates your actual TDEE from food logs and weight data, no generic formulas. Exercise substitution and weight recommendations use deterministic algorithms for speed and accuracy. All AI runs server-side.' },
    { q: 'Can I use TrainForge without a gym?', a: 'Absolutely. Sign up as an individual, tell us what equipment you have (even just bodyweight), and the AI builds a program around it.' },
    { q: 'What is adaptive nutrition?', a: 'TrainForge calculates your actual Total Daily Energy Expenditure (TDEE) from the food you log and your weight trend. Every week, your macro targets adjust automatically. No guessing with online calculators. You also get coaching insights like "protein under target 4 of 7 days."' },
    { q: 'Can I use TrainForge at different gyms?', a: 'Yes. Select multiple gym profiles (Planet Fitness, home gym, etc.) and your equipment merges. You can even convert a scheduled workout to a different gym\'s equipment with one tap. The AI finds equivalent exercises that match your available gear.' },
    { q: 'How does white-label branding work?', a: 'Gym owners upload their logo, set colors, and get a custom subdomain (yourgym.mytrainforge.com). Members see your brand, not ours.' },
    { q: 'What data do you collect?', a: 'Only what you enter: workout logs, body measurements, food entries, and profile info. We don\'t sell data, run ads, or share with third parties. See our privacy policy for full details.' },
    { q: 'Can trainers see their members\' data?', a: 'Yes. Trainers see workout history, body metrics, adherence %, and AI coaching summaries for their assigned members. They can also write private notes and generate or edit programs.' },
    { q: 'Do I need to install an app?', a: 'TrainForge is a PWA (progressive web app). Add it to your home screen from the browser. It works like a native app with no App Store download required.' },
    { q: 'How do I get my gym on TrainForge?', a: 'Contact us through the beta request form. We\'ll set up your gym, configure your equipment and branding, and help you invite your first members.' },
  ];

  return (
    <section style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', margin: '0 0 48px', fontSize: 32 }}>Frequently asked questions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {items.map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', textAlign: 'left', padding: '18px 0', minHeight: 0,
          background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
          fontSize: 15, fontWeight: 500,
        }}
      >
        <span>{q}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 18, flexShrink: 0 }}>{open ? '-' : '+'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 0 18px', color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.6 }}>
          {a}
        </div>
      )}
    </div>
  );
}

function GetStarted() {
  return (
    <section id="get-started" style={{ padding: '80px 24px', background: 'var(--bg-elev)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <h2 style={{ fontSize: 32, margin: '0 0 16px' }}>Bring TrainForge to your gym</h2>
        <p style={{ color: 'var(--text-dim)', margin: '0 0 32px', fontSize: 17, lineHeight: 1.6 }}>
          We're onboarding a small number of partner gyms during beta. You get hands-on setup, founder pricing, and direct input on the roadmap.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/contact"><button className="primary" style={{ padding: '14px 32px', fontSize: 16 }}>Request beta access</button></Link>
          <Link to="/login"><button style={{ padding: '14px 32px', fontSize: 16 }}>Try it as an individual</button></Link>
        </div>
      </div>
    </section>
  );
}

function AudienceCard({ title, tag, desc, items, cta, ctaLink }) {
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 28 }}>
      <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 8 }}>{tag}</div>
      <h3 style={{ margin: '0 0 8px', fontSize: 24 }}>{title}</h3>
      <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.5, margin: '0 0 16px' }}>{desc}</p>
      <ul style={{ margin: '0 0 20px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--text-dim)', fontSize: 14 }}>
        {items.map((item, i) => <li key={i} style={{ paddingLeft: 16, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'var(--accent)' }}>-</span> {item}</li>)}
      </ul>
      <Link to={ctaLink}><button className="primary" style={{ width: '100%' }}>{cta}</button></Link>
    </div>
  );
}

function FeatureCard({ title, items }) {
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 20 }}>{title}</h3>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--text-dim)', fontSize: 14 }}>
        {items.map((item, i) => <li key={i} style={{ paddingLeft: 16, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'var(--accent)' }}>-</span> {item}</li>)}
      </ul>
    </div>
  );
}


function PriceCard({ name, price, desc, features, highlighted }) {
  return (
    <div style={{
      background: 'var(--bg)',
      border: '1px solid ' + (highlighted ? 'var(--accent)' : 'var(--border)'),
      borderRadius: 'var(--radius-lg)',
      padding: 24,
      textAlign: 'left',
      position: 'relative',
    }}>
      {highlighted && <div style={{ position: 'absolute', top: -10, right: 16, background: 'var(--accent)', color: 'var(--bg)', fontSize: 10, padding: '4px 10px', borderRadius: 4, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>Most popular</div>}
      <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>{name}</h3>
      <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{price}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{desc}</div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--text-dim)' }}>
        {features.map((f, i) => <li key={i} style={{ paddingLeft: 18, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'var(--success)' }}>✓</span> {f}</li>)}
      </ul>
    </div>
  );
}
